import { useEffect, useState } from "react";
import {
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  SavedViewsBar,
  Switch,
  type SavedView,
} from "@fox1ck-org/ui";
import { toast } from "@fox1ck-org/ui/toast";
import type { ReportView } from "@gen/tracker/v1/reporting_pb";

import { formatDayRange } from "@/lib/format";
import { useT, type TKey } from "@/lib/i18n";
import { useCreateReportView } from "@/lib/services/report-view-service";
import { useDeleteViewWithUndo } from "@/lib/hooks/use-delete-view";
import { useGetWorkspace } from "@/lib/services/workspace-service";
import { toastFailure } from "@/lib/plan";
import { activeSet, BUILTIN_SETS, pickColumns, columnLabel } from "./columns";
import { DIM_OP_WORD } from "./filter-panel";
import { BUILTIN_VIEWS, DEFAULT_VIEW } from "./views";
import type { ReportEngine } from "./engine";

/**
 * «По объявлениям · По гео и часам · Ленды · Офферы по партнёркам · Ещё 3 ▾»
 * (доски Report, ReportSaved): the package's SavedViewsBar over the four
 * built-in reports and every saved one — mine and the workspace's (a pin).
 * What does not fit moves into «Ещё N ▾»; the open report always stays in
 * the row, with «•» while it has unsaved changes. Only its author deletes a
 * saved report (the × on the open chip).
 */
export function ReportViewsBar({
  current,
  saved,
  dirty,
  onOpen,
  onSave,
  className,
}: {
  current: string;
  saved: ReportView[];
  dirty: boolean;
  onOpen: (id: string) => void;
  onSave: () => void;
  className?: string;
}) {
  const t = useT();
  const remove = useDeleteViewWithUndo();
  const mark = (id: string, name: string) => (id === current && dirty ? `${name} •` : name);
  const views: SavedView[] = [
    ...BUILTIN_VIEWS.map((v) => ({ id: v.id, name: mark(v.id, t(v.label)) })),
    ...saved.map((v) => ({ id: v.id, name: mark(v.id, v.name), isShared: v.scope === "workspace" })),
  ];
  const openSaved = saved.find((v) => v.id === current);

  return (
    <SavedViewsBar
      className={className}
      variant="chips"
      overflow="menu"
      views={views}
      activeId={current}
      allLabel={null}
      onSelect={(v) => onOpen(v?.id ?? DEFAULT_VIEW.id)}
      onCreate={onSave}
      onDelete={
        openSaved?.canEdit
          ? () =>
              remove(openSaved, {
                deleted: t("rep.views.deleted", { name: openSaved.name }),
                onDeleted: () => onOpen(DEFAULT_VIEW.id),
                onRestored: (v) => onOpen(v.id),
              })
          : undefined
      }
      labels={{
        toolbar: t("rep.views"),
        create: t("rep.views.save"),
        createAria: t("rep.views.createAria"),
        view: (v) => t(v.isShared ? "rep.views.chipShared" : "rep.views.chip", { name: v.name }),
        delete: (name) => t("rep.views.delete", { name }),
        more: (n) => t("rep.views.more", { n }),
        moreAria: (n) => t("rep.views.moreAria", { n }),
      }}
    />
  );
}

/**
 * «Сохранить отчёт» (доска ReportSave): the name, what is saved in words,
 * «Запомнить период» (else the Top bar's), and whether the workspace sees
 * it — others open and copy it, only the author changes it. A taken name is
 * saved as «(2)» by the server, never refused.
 */
export function SaveReportDialog({
  engine,
  open,
  onOpenChange,
  onSaved,
  withPinned,
  nameHint,
}: {
  engine: ReportEngine;
  open: boolean;
  onOpenChange: (o: boolean) => void;
  onSaved: (view: ReportView) => void;
  /** An embedded engine saves its pin as a filter — the report opens in «Отчёты». */
  withPinned?: boolean;
  nameHint?: string;
}) {
  const t = useT();
  const create = useCreateReportView();
  const workspace = useGetWorkspace().data;
  const [name, setName] = useState("");
  const [keepPeriod, setKeepPeriod] = useState(false);
  const [shared, setShared] = useState(true);
  useEffect(() => {
    if (open) setName(nameHint ?? "");
  }, [open, nameHint]);
  const { state } = engine;
  const p = state.params;
  const filters = withPinned ? engine.query : state.filters;
  const set = activeSet(state.metrics, engine.columnSets);
  const setLabel = set ? ((BUILTIN_SETS as readonly string[]).includes(set) ? t(`rep.set.${set}` as TKey) : engine.columnSets.find((v) => v.id === set)?.name) : undefined;
  const cols = pickColumns(state.metrics, engine.schema);
  const filterText =
    [
      ...filters.dims.map((d) => `${engine.dimLabel(d.key)}: ${DIM_OP_WORD(t, d.op)}${d.values.map((v) => engine.names.name(d.key, v)).join(", ")}`),
      ...filters.metrics.map((m) => {
        const c = cols.find((x) => x.key === m.key);
        return c ? columnLabel(t, c) : m.key;
      }),
    ].join(" · ") || t("rep.save.noFilters");

  const rows: [string, string][] = [
    [t("rep.grouping"), state.dims.map(engine.dimLabel).join(" › ")],
    [t("rep.filters"), filterText],
    [t("rep.save.columns"), setLabel ? t("rep.save.columns.set", { set: setLabel, n: cols.length }) : t("rep.save.columns.own", { n: cols.length })],
    [t("rep.basis"), `${t(`rep.basis.${engine.basis}` as TKey)}${p.compare !== "off" ? ` · ${t(p.compare === "year" ? "rep.save.compareYear" : "rep.save.comparePrev")}` : ""}`],
  ];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-135">
        <DialogHeader>
          <DialogTitle>{t("rep.save.title")}</DialogTitle>
          <DialogDescription>{t("rep.save.body")}</DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="report-name" textCase="none">
              {t("rep.save.name")}
            </Label>
            <Input id="report-name" font="sans" value={name} onChange={(e) => setName(e.target.value)} placeholder={t("rep.save.placeholder")} autoFocus maxLength={80} />
          </div>
          <div className="flex flex-col gap-1.5">
            <span className="text-xs text-muted-foreground">{t("rep.save.what")}</span>
            <dl className="grid grid-cols-[--spacing(27.5)_minmax(0,1fr)] gap-x-3 gap-y-1.5 text-sm">
              {rows.map(([k, v]) => (
                <div key={k} className="contents">
                  <dt className="text-muted-foreground">{k}</dt>
                  <dd className="truncate">{v}</dd>
                </div>
              ))}
            </dl>
          </div>
          <label className="flex items-center gap-2 text-sm">
            <Checkbox checked={keepPeriod} onCheckedChange={(c) => setKeepPeriod(c === true)} />
            {t("rep.save.keepPeriod", { period: formatDayRange(p.from, p.to) })}
            <span className="text-xs text-muted-foreground">{t("rep.save.keepPeriod.else")}</span>
          </label>
          <label className="flex items-start gap-3 rounded-lg border border-border p-3">
            <Switch checked={shared} onCheckedChange={setShared} className="mt-0.5" />
            <span className="flex flex-col">
              <span className="text-sm font-medium">{t("rep.save.shared", { workspace: workspace?.name ?? "" })}</span>
              <span className="text-xs text-muted-foreground">{t("rep.save.shared.hint")}</span>
            </span>
          </label>
        </div>
        <DialogFooter className="items-center">
          <span className="mr-auto text-xs text-muted-foreground">{t("rep.save.nameTaken")}</span>
          <Button variant="secondary" onClick={() => onOpenChange(false)}>
            {t("rep.cancel")}
          </Button>
          <Button
            disabled={!name.trim() || create.isPending}
            onClick={() =>
              create.mutate(
                { name: name.trim(), definition: engine.definition({ keepPeriod, withPinned }), workspaceShared: shared },
                {
                  onSuccess: (v) => {
                    if (!v) return;
                    toast.success(t("rep.saved", { name: v.name }));
                    onOpenChange(false);
                    onSaved(v);
                  },
                  onError: (e) => toastFailure(e, t("rep.saveFailed")),
                },
              )
            }
          >
            {t("rep.save.submit")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** A name, asked once — «Сохранить как набор» of the column picker. */
export function NameDialog({ open, onOpenChange, title, initial, onSubmit }: { open: boolean; onOpenChange: (o: boolean) => void; title: string; initial: string; onSubmit: (name: string) => void }) {
  const t = useT();
  const [name, setName] = useState(initial);
  useEffect(() => {
    if (open) setName(initial);
  }, [open, initial]);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-105">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        <Input font="sans" value={name} onChange={(e) => setName(e.target.value)} autoFocus maxLength={80} aria-label={t("rep.save.name")} onKeyDown={(e) => e.key === "Enter" && name.trim() && onSubmit(name.trim())} />
        <DialogFooter>
          <Button variant="secondary" onClick={() => onOpenChange(false)}>
            {t("rep.cancel")}
          </Button>
          <Button disabled={!name.trim()} onClick={() => onSubmit(name.trim())}>
            {t("rep.save.submit")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
